import api from '@/utils/api';

export default {
    data: () => ({employeeHistory: null}),
    created() { this._employeeHistoryCache = new Map(); this._employeeHistoryRequest = 0; },
    beforeDestroy() { this._employeeHistoryRequest++; },
    watch: {
        periodMonth() { this.resetEmployeeHistory(); },
        summary(value, previous) {
            if (value && previous && value.period && previous.period && value.period.starts_on === previous.period.starts_on) {
                this._employeeHistoryCache.clear();
                if (this.employeeHistory) this._employeeHistoryRefresh = this.refreshEmployeeHistory();
            } else this.resetEmployeeHistory();
        }
    },
    methods: {
        historyKey(run, group, line) { return `${run.id}:${group.key}:${line.id}`; },
        historyRow() {
            return this.employeeHistory && this.$el.querySelector
                ? Array.from(this.$el.querySelectorAll('[data-payroll-line]')).find(row => row.getAttribute('data-payroll-line') === this.employeeHistory.key) : null;
        },
        historyAnchor() {
            const row = this.historyRow();
            if (!row || typeof window === 'undefined') return null;
            let scroll = row.parentElement;
            while (scroll && (scroll.scrollHeight <= scroll.clientHeight || !/(auto|scroll)/.test(getComputedStyle(scroll).overflowY))) scroll = scroll.parentElement;
            return {row, scroll: scroll || document.scrollingElement, top: row.getBoundingClientRect().top};
        },
        restoreHistoryAnchor(anchor, opening = false) {
            if (!anchor || !anchor.row.isConnected) return;
            const rect = anchor.row.getBoundingClientRect();
            const bottom = anchor.scroll === document.scrollingElement ? window.innerHeight : anchor.scroll.getBoundingClientRect().bottom;
            const reserved = this.editVisible && this.editPinned ? this.editPanelHeight + 24 : 0;
            anchor.scroll.scrollTop += rect.top - (opening ? Math.max(100, bottom - reserved - Math.min(rect.height, 180) - 20) : anchor.top);
        },
        resetEmployeeHistory() {
            this.cancelPayrollRowReturn();
            const anchor = this.historyAnchor();
            const request = ++this._employeeHistoryRequest;
            this._employeeHistoryCache.clear(); this.employeeHistory = null;
            this.$nextTick(() => { if (request === this._employeeHistoryRequest) this.restoreHistoryAnchor(anchor); });
        },
        closeEmployeeHistory(returnToEmployee = false) {
            this.cancelPayrollRowReturn();
            const target = {...this.payrollReturnTarget(), key: this.employeeHistory && this.employeeHistory.key};
            const anchor = this.historyAnchor();
            const request = ++this._employeeHistoryRequest;
            this.employeeHistory = null;
            if (returnToEmployee) return this.returnToPayrollRow(target);
            this.$nextTick(() => { if (request === this._employeeHistoryRequest) this.restoreHistoryAnchor(anchor); });
        },
        async toggleEmployeeHistory(run, group, line) {
            const key = this.historyKey(run, group, line);
            if (this.employeeHistory && this.employeeHistory.key === key) { await this.closeEmployeeHistory(true); return; }
            this.cancelPayrollRowReturn();
            if (this.editVisible && this.editRowKey !== key) {
                if (!await this.allowEditorLeave()) return;
                await this.finishEditor();
            }
            const row = this.$el.querySelectorAll ? Array.from(this.$el.querySelectorAll('[data-payroll-line]')).find(item => item.getAttribute('data-payroll-line') === key) : null;
            this._employeeHistoryRequest++;
            const cacheKey = `${this.periodMonth}:${line.employee_id}`, cached = this._employeeHistoryCache.get(cacheKey);
            this.employeeHistory = {key, cacheKey, employeeId: line.employee_id, name: line.employee_name_snapshot,
                months: cached ? cached.months : [], next: cached ? cached.next : this.periodMonth, loaded: !!cached, loading: false, error: '', refreshFailed: false};
            const anchor = this.historyAnchor();
            if (row && anchor) anchor.top = row.getBoundingClientRect().top;
            if (!cached) await this.loadEmployeeHistory(true);
            else { await this.$nextTick(); if (this.employeeHistory && this.employeeHistory.key === key) this.restoreHistoryAnchor(anchor, true); }
        },
        async refreshEmployeeHistory() {
            const state = this.employeeHistory;
            if (!state) return;
            const request = ++this._employeeHistoryRequest, anchor = this.historyAnchor();
            const count = Math.max(6, state.months.length);
            state.loading = true; state.error = ''; state.refreshFailed = false;
            let next = this.periodMonth, months = [];
            try {
                while (next && months.length < count) {
                    const {data} = await api.get(`/api/payroll/employees/${state.employeeId}/history`, {params: {before_month: next, limit: 6}});
                    if (request !== this._employeeHistoryRequest || this.employeeHistory !== state) return;
                    months = [...data.months, ...months].filter((month, i, values) => values.findIndex(other => other.month === month.month) === i);
                    if (data.next_before_month && data.next_before_month >= next) throw Error('Invalid history cursor');
                    next = data.next_before_month;
                }
                state.months = months.sort((a, b) => a.month.localeCompare(b.month)); state.next = next; state.loaded = true;
                this._employeeHistoryCache.set(state.cacheKey, {months: state.months, next});
            } catch (error) { if (request === this._employeeHistoryRequest) { state.refreshFailed = true; state.error = 'Не удалось обновить историю. Показаны ранее загруженные данные.'; } }
            finally { if (request === this._employeeHistoryRequest) { state.loading = false; await this.$nextTick(); if (request === this._employeeHistoryRequest) this.restoreHistoryAnchor(anchor); } }
        },
        async loadEmployeeHistory(opening = false) {
            const state = this.employeeHistory;
            if (!state || state.loading) return;
            if (state.refreshFailed) return this.refreshEmployeeHistory();
            if (!state.next) return;
            const request = ++this._employeeHistoryRequest, anchor = this.historyAnchor();
            state.loading = true; state.error = '';
            try {
                const {data} = await api.get(`/api/payroll/employees/${state.employeeId}/history`, {params: {before_month: state.next, limit: 6}});
                if (request !== this._employeeHistoryRequest || this.employeeHistory !== state) return;
                state.months = [...data.months, ...state.months].filter((month, index, rows) => rows.findIndex(other => other.month === month.month) === index)
                    .sort((a, b) => a.month.localeCompare(b.month));
                state.next = data.next_before_month; state.loaded = true;
                this._employeeHistoryCache.set(state.cacheKey, {months: state.months, next: state.next});
            } catch (error) {
                if (request === this._employeeHistoryRequest) state.error = error.response && error.response.data && error.response.data.msg || 'Не удалось загрузить историю сотрудника.';
            } finally {
                if (request === this._employeeHistoryRequest) {
                    state.loading = false; await this.$nextTick();
                    if (request === this._employeeHistoryRequest) this.restoreHistoryAnchor(anchor, opening);
                }
            }
        }
    }
};
